import { makeObservable, observable, action, computed } from 'mobx';

/**
 * Global MobX Store for Chat Document Management
 * Manages documents created from chat across all pages/modules
 * Note: Separate from v2/documents/DocumentsStore (for drives system)
 */
class ChatDocumentsStore {
  documents = [];
  currentDocumentId = null;

  constructor() {
    makeObservable(this, {
      documents: observable,
      currentDocumentId: observable,
      addDocument: action,
      updateDocument: action,
      deleteDocument: action,
      setCurrentDocument: action,
      clearCurrentDocument: action,
      sortedDocuments: computed,
      documentCount: computed,
    });
  }

  /**
   * Add a new document to the store
   * @param {Object} docData - Document data
   * @returns {string} - Document ID
   */
  addDocument(docData) {
    const id = this.generateId();
    const now = new Date();
    
    // Ensure content is always a string
    const contentString = typeof docData.content === 'string'
      ? docData.content
      : String(docData.content || '');
    
    const newDocument = {
      id,
      title: docData.title || 'Untitled Document',
      content: contentString,
      createdAt: now,
      lastEditedAt: now,
      wordCount: this.countWords(contentString),
      preview: this.generatePreview(contentString),
      tags: docData.tags || [],
      agentUuid: docData.agentUuid || null,
      conversationId: docData.conversationId || null,
      module: docData.module || 'HomePage',
    };

    this.documents.push(newDocument);
    return id;
  }

  /**
   * Update an existing document
   * @param {string} id - Document ID
   * @param {Object} updates - Fields to update
   */
  updateDocument(id, updates) {
    const docIndex = this.documents.findIndex(doc => doc.id === id);
    
    if (docIndex === -1) {
      console.warn(`Document with id ${id} not found`);
      return;
    }

    const doc = this.documents[docIndex];
    
    // Auto-calculate metadata if content changed
    if (updates.content !== undefined && updates.content !== null) {
      // Ensure content is a string
      const contentString = typeof updates.content === 'string' 
        ? updates.content 
        : String(updates.content || '');
      
      updates.lastEditedAt = new Date();
      updates.wordCount = this.countWords(contentString);
      updates.preview = this.generatePreview(contentString);
      updates.content = contentString; // Ensure stored content is always a string
    }

    this.documents[docIndex] = {
      ...doc,
      ...updates,
    };
  }

  /**
   * Delete a document
   * @param {string} id - Document ID
   */
  deleteDocument(id) {
    this.documents = this.documents.filter(doc => doc.id !== id);
    
    // Clear current document if it was deleted
    if (this.currentDocumentId === id) {
      this.currentDocumentId = null;
    }
  }

  /**
   * Get a specific document by ID
   * @param {string} id - Document ID
   * @returns {Object|null} - Document or null
   */
  getDocument(id) {
    return this.documents.find(doc => doc.id === id) || null;
  }

  /**
   * Set the currently active document
   * @param {string} id - Document ID
   */
  setCurrentDocument(id) {
    this.currentDocumentId = id;
  }

  /**
   * Clear the currently active document
   */
  clearCurrentDocument() {
    this.currentDocumentId = null;
  }

  /**
   * Search documents by title, content, or tags
   * @param {string} query - Search query
   * @returns {Array} - Filtered documents
   */
  searchDocuments(query) {
    if (!query || query.trim() === '') {
      return this.sortedDocuments;
    }

    const lowerQuery = query.toLowerCase();
    
    return this.documents.filter(doc => {
      return (
        doc.title.toLowerCase().includes(lowerQuery) ||
        doc.content.toLowerCase().includes(lowerQuery) ||
        doc.tags.some(tag => tag.toLowerCase().includes(lowerQuery)) ||
        doc.preview.toLowerCase().includes(lowerQuery)
      );
    }).sort((a, b) => b.lastEditedAt - a.lastEditedAt);
  }

  /**
   * Get documents by tag
   * @param {string} tag - Tag name
   * @returns {Array} - Documents with that tag
   */
  getDocumentsByTag(tag) {
    return this.documents.filter(doc => 
      doc.tags.includes(tag)
    ).sort((a, b) => b.lastEditedAt - a.lastEditedAt);
  }

  /**
   * Get all unique tags
   * @returns {Array} - Array of unique tags
   */
  get allTags() {
    const tagsSet = new Set();
    this.documents.forEach(doc => {
      doc.tags.forEach(tag => tagsSet.add(tag));
    });
    return Array.from(tagsSet).sort();
  }

  /**
   * Computed: Documents sorted by last edited (most recent first)
   */
  get sortedDocuments() {
    return this.documents.slice().sort((a, b) => {
      return b.lastEditedAt - a.lastEditedAt;
    });
  }

  /**
   * Computed: Total document count
   */
  get documentCount() {
    return this.documents.length;
  }

  /**
   * Get current document object
   */
  get currentDocument() {
    if (!this.currentDocumentId) return null;
    return this.getDocument(this.currentDocumentId);
  }

  // Utility methods

  /**
   * Generate unique ID
   * @returns {string} - Unique ID
   */
  generateId() {
    return `doc_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Count words in text
   * @param {string} text - Text content
   * @returns {number} - Word count
   */
  countWords(text) {
    if (!text || typeof text !== 'string') return 0;
    const cleaned = text.replace(/[#*`_\[\]()]/g, '').trim();
    if (!cleaned) return 0;
    return cleaned.split(/\s+/).filter(Boolean).length;
  }

  /**
   * Generate preview from content
   * @param {string} content - Document content
   * @returns {string} - Preview text
   */
  generatePreview(content) {
    // Handle non-string content
    if (!content || typeof content !== 'string') return '';
    
    // Remove markdown syntax for cleaner preview
    const plainText = content
      .replace(/[#*`_]/g, '')
      .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1')
      .trim();
    
    const maxLength = 200;
    return plainText.length > maxLength 
      ? plainText.substring(0, maxLength) + '...'
      : plainText;
  }

  /**
   * Add tag to document
   * @param {string} id - Document ID
   * @param {string} tag - Tag to add
   */
  addTag(id, tag) {
    const doc = this.getDocument(id);
    if (!doc) return;
    
    const normalizedTag = tag.trim().toLowerCase();
    if (!normalizedTag || doc.tags.includes(normalizedTag)) return;
    
    this.updateDocument(id, {
      tags: [...doc.tags, normalizedTag]
    });
  }

  /**
   * Remove tag from document
   * @param {string} id - Document ID
   * @param {string} tag - Tag to remove
   */
  removeTag(id, tag) {
    const doc = this.getDocument(id);
    if (!doc) return;
    
    this.updateDocument(id, {
      tags: doc.tags.filter(t => t !== tag)
    });
  }
}

export default new ChatDocumentsStore();

